import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {Button} from 'primereact/button'
import {Chart} from 'primereact/chart'
import {SelectButton} from 'primereact/selectbutton'
import {ToggleButton} from 'primereact/togglebutton'
import {useCallback, useMemo, useRef} from 'react'
import {useParams} from 'react-router-dom'
import {SystemRole} from '../../Constants'
import {ReactComponent as FaClimbing} from '../../assets/icons/FaClimbing.svg'
import {useAppConfig} from '../../context/app/AppContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {useAuth} from '../authentication/AuthProvider'
import {ECGDeviceTypes} from '../ecg/ECGConstants'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {
    useListHealthRecordsInDate,
    useListNewestHealthRecords,
} from '../observations/query-keys/ObsevationKeysManager'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import WatchDialog from '../watch-config/WatchDialog'
import {syncActivitiesData} from './ExerciseEndpoint'

const fontSize = 'text-xs sm:text-xs md:text-md lg:text-md'

const ExercisesChart = ({
    isToggle = false,
    hideToggleButton = false,
    onToggleActivities = () => {},
}) => {
    useSignals()
    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    //#endregion

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region Ref
    const chartRef = useRef(null)
    const watchDialog = useRef()
    //#endregion

    //#region State
    const selectedBtnValue = useSignal('steps')
    const viewMode = useSignal('week')
    // eslint-disable-next-line no-unused-vars
    const toggle = useSignal(false)
    const selectedDate = useSignal(null)
    const date = useSignal(null)
    const type = useSignal('')
    //#endregion

    const documentStyle = getComputedStyle(document.documentElement)

    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        if (!idString) {
            return null
        }
        return parseInt(idString)
    }, [idString])
    //#endregion

    //#region Query

    const {data: activityRecordsData, isFetched} = useListNewestHealthRecords(
        user?.id,
        userRole === SystemRole.USER ? user?.id : id,
        selectedBtnValue.value,
        !!user?.id && !!selectedBtnValue.value,
    )

    const {data: activityRecordsDataInDay, isFetched: isDayFetched} =
        useListHealthRecordsInDate(
            user?.id,
            userRole === SystemRole.USER ? user?.id : id,
            selectedDate.value,
            type.value,
            !!user?.id && !!selectedDate.value && !!type.value,
        )
    //#endregion

    //#region Handle Function
    const handleSelectButtonChange = e => {
        if (e.value) {
            selectedBtnValue.value = e.value
            toggle.value = true
        }

        viewMode.value = 'week'
        selectedDate.value = null
    }

    const handleOnClickDate = useCallback(
        (evt, item) => {
            if (viewMode.value === 'day') {
                return
            }
            const eventDate = evt?.chart?.tooltip?.title
                ? evt.chart?.tooltip?.title[0]
                : ''
            const typeValue = evt?.chart?.config?._config?.data?.datasets[0]
                ?.label
                ? evt?.chart?.config?._config?.data?.datasets[0]?.label
                : ''
            if (!!eventDate) {
                let dateFmt = eventDate?.replace(/ /g, ' ')
                if (dateFmt && typeValue) {
                    date.value = dateFmt
                    type.value = typeValue
                    selectedDate.value = dateFmt
                    viewMode.value = 'day'
                }
            }
        },
        [date, selectedDate, type, viewMode],
    )

    const handleSyncData = async () => {
        showNotification(
            severityNotification.info,
            'Synchronizing signal sent successfully !',
        )
        watchDialog?.current.showForm()
        await syncActivitiesData(id)
    }
    //#endregion

    //#region Memo
    const chartOptions = [
        {label: translateCommons('Steps'), value: 'steps'},
        {label: translateCommons('Sleep Duration'), value: 'sleep'},
        {label: translateCommons('Calories'), value: 'calories'},
        {label: translateCommons('Heart Rate'), value: 'heartRate'},
        {label: translateCommons('SP.O2'), value: 'oxygen'},
        {label: translateCommons('Temperature'), value: 'temperature'},
    ]

    const dayTimeLabels = useMemo(() => {
        let tmp = []

        for (let i = 0; i < 24; i++) {
            let timeLabel = i < 10 ? `0${i}` : i
            timeLabel += ':00'

            tmp.push(timeLabel)
        }

        return tmp
    }, [])
    const convertSleepDuration = sleepDurationInMillis => {
        if (!sleepDurationInMillis || sleepDurationInMillis === 0) {
            return 0
        }

        const sleepInSeconds = sleepDurationInMillis / 1000
        let minutes = Math.floor(sleepInSeconds / 60)
        const leftSeconds = sleepInSeconds % 60

        if (leftSeconds >= 30) {
            minutes++
        }

        return minutes
    }

    const weekDataChart = useMemo(() => {
        if (!activityRecordsData) {
            return {}
        }

        let labels = []
        let data = []

        for (let i = 0; i < activityRecordsData?.length; i++) {
            if (!!activityRecordsData[i]?.date) {
                labels.push(activityRecordsData[i].date)
            }
        }

        labels.sort((a, b) => {
            a = a.split('/').join('')
            b = b.split('/').join('')
            return a.localeCompare(b)
        })

        for (let i = 0; i < labels?.length; i++) {
            const filterDataList = activityRecordsData.filter(
                x => x.date === labels[i],
            )
            if (!filterDataList || filterDataList.length === 0) {
                data.push(0)
                continue
            } else {
                const filterData = filterDataList[0]
                if (!!filterData) {
                    if (selectedBtnValue.value === 'sleep') {
                        if (
                            !!filterData?.deviceBrand &&
                            filterData.deviceBrand === ECGDeviceTypes.samsung
                        ) {
                            data.push(convertSleepDuration(filterData?.value))
                        } else {
                            data.push(filterData?.value)
                        }
                    } else {
                        data.push(filterData?.value)
                    }
                }
            }
        }

        return {
            labels: labels,
            datasets: [
                {
                    label: selectedBtnValue.value
                        ? convertKeyToLabel(selectedBtnValue.value)
                        : '',
                    data: data,
                    fill: false,
                    borderColor:
                        documentStyle.getPropertyValue('--primary-color'),
                    tension: 0.4,
                },
            ],
        }
    }, [activityRecordsData, documentStyle, selectedBtnValue.value])

    const dateDataChart = useMemo(() => {
        if (!activityRecordsDataInDay) {
            return {}
        }

        const convertedData = activityRecordsDataInDay.map(data => data?.value)

        return {
            labels: dayTimeLabels,
            datasets: [
                {
                    label: type.value ? convertKeyToLabel(type.value) : '',
                    data: convertedData,
                    fill: true,
                    backgroundColor: '#42A5F5',
                    borderColor:
                        documentStyle.getPropertyValue('--primary-color'),
                    tension: 0.4,
                },
            ],
        }
    }, [activityRecordsDataInDay, dayTimeLabels, documentStyle, type.value])

    const options = useMemo(() => {
        return {
            maintainAspectRatio: false,
            aspectRatio: 0.2,
            scales: {
                y: {
                    beginAtZero: true,
                },
            },
            plugins: {
                legend: {
                    display: false,
                    events: [],
                },
            },
            legend: {
                display: false,
                events: [],
            },
            onClick: (evt, item) => handleOnClickDate(evt, item),
        }
    }, [handleOnClickDate])
    //#endregion

    //#region Event Listener
    // useEffect(() => {
    //     if (!!id) {
    //         refetchEvent.on('REFETCH-DATA-TABLE-Health-Activity-' + id, () => {
    //             refetch()
    //         })

    //         return () => {
    //             refetchEvent.removeListener(
    //                 'REFETCH-DATA-TABLE-Health-Activity-' + id,
    //             )
    //         }
    //     }
    // }, [id, refetch])
    //#endregion

    return (
        <div className="grid col-12 p-0">
            <div className="grid col-12 font-bold text-center p-0 border-round m-2 clearfix">
                <div className="flex justify-content-between align-items-center col-12 mb-3">
                    <div>
                        <h5 className="font-bold mb-0 m-0 p-0">
                            <span className="mr-2">
                                <FaClimbing
                                    className="font-bold text-lg"
                                    style={{width: '20px', height: '20px'}}
                                />
                            </span>
                            {translateCommons('Activity Records')}
                        </h5>
                    </div>

                    <div>
                        <div className="flex justify-content-between col-12">
                            <div className="mr-3">
                                <Button
                                    className={`${fontSize}`}
                                    label={translateCommons('Sync Data')}
                                    icon="pi pi-sync"
                                    onClick={() => handleSyncData()}
                                />
                            </div>
                            {!hideToggleButton && (
                                <ToggleButton
                                    onLabel={translateCommons('Table')}
                                    offLabel={translateCommons('Chart')}
                                    onIcon="pi pi-table"
                                    offIcon="pi pi-chart-line"
                                    checked={isToggle}
                                    onChange={e => onToggleActivities(e.value)}
                                    className={`w-9rem ${fontSize}`}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-12 flex justify-content-center align-items-center">
                    <SelectButton
                        value={selectedBtnValue.value}
                        options={chartOptions}
                        onChange={handleSelectButtonChange}
                    />
                </div>

                {viewMode.value === 'day' && selectedDate.value && (
                    <div className="col-12 mb-3">
                        <h5>
                            {translateCommons('Selected Date')}: {selectedDate}
                        </h5>
                    </div>
                )}

                <Chart
                    ref={chartRef}
                    className="col-12 px-0"
                    style={{maxHeight: '60vh'}}
                    type={viewMode.value === 'week' ? 'line' : 'bar'}
                    data={
                        viewMode.value === 'week'
                            ? isFetched && weekDataChart
                            : isDayFetched && dateDataChart
                    }
                    options={options}
                />
            </div>
            <WatchDialog ref={watchDialog} />
        </div>
    )
}

export default ExercisesChart
